import type { GaiaExpertAssistAppName } from "@expert/shared-types";

/**
 * Tunables for the dead-air speculative assist.
 *
 * The one operator-facing knob is the silence threshold, which is a server-side config value
 * (`EWP-deadAirTimeout`, in seconds). Everything else here is a client constant; move any of these to
 * config later if they need to be tuned without a deploy.
 */

/** Barvidently feature-toggle key holding the silence threshold, in seconds. Ships with defaultValue 45. */
export const DEAD_AIR_FLAG_KEY = "EWP-deadAirTimeout";

/**
 * Barvidently feature-toggle key for the dark launch. When true, detection and the classification ask run
 * as normal on the `EWP-deadAirTimeout` threshold, but a suggestion that would surface is withheld from
 * the expert and reported as `DeadAirAssistWouldHaveSurfaced` instead. Only `true` enables it; missing or
 * any other value leaves the assist live.
 */
export const DEAD_AIR_DARK_LAUNCH_FLAG_KEY = "EWP-isDeadAirDarkLaunchEnabled";

/**
 * The threshold the config ships with. Documented here only for readers/tests; the client does NOT
 * fall back to this when the flag is absent — a missing/zero value means the feature is off, so the
 * config's presence is what enables it (see useDeadAirAssist).
 */
export const DEAD_AIR_DEFAULT_TIMEOUT_SECONDS = 45;

/** Minimum model confidence (0..1) required to surface a suggestion. "High confidence" per product. */
export const DEAD_AIR_CONFIDENCE_THRESHOLD = 0.8;

/** How many of the most recent transcript snippets are sent to the model as context. */
export const DEAD_AIR_TRANSCRIPT_WINDOW = 12;

/** How many of the most recent snippets (PII-scrubbed) a dark-launch event carries, so a reviewer can see
 *  what was said right before the silence without scrubbing the call recording. */
export const DEAD_AIR_DARK_LAUNCH_TRANSCRIPT_TAIL = 3;

/** Cap on the GAIA round-trip for a classification request. */
export const DEAD_AIR_REQUEST_TIMEOUT_MS = 15_000;

/** Minimum time between classification asks on a single call, so a long silence asks at most once. */
export const DEAD_AIR_ASK_COOLDOWN_MS = 30_000;

/**
 * How long a completed correlationId is kept as a tombstone after it stops being tracked as in-flight.
 * The GAIA reply can arrive after the request has already settled (via `DEAD_AIR_REQUEST_TIMEOUT_MS` or
 * an abort) because the WebSocket message was already sent and cannot be recalled. Comfortably longer
 * than any plausible GAIA reply, so a response arriving after the 15s request timeout is still
 * recognized and suppressed rather than rendering as an unrequested visible bubble.
 */
export const DEAD_AIR_TOMBSTONE_TTL_MS = 60_000;

/**
 * The GAIA app the hidden ask reuses. Voice expert-assist ("expert-assist") is intentional: the ask
 * rides the existing orchestrator-invoke path (no backend change), and its reply is kept invisible via
 * the correlationId registry rather than a new backend app.
 */
export const DEAD_AIR_APP_NAME: GaiaExpertAssistAppName = "expert-assist";
